import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, usersAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, UserX, UserCheck, Loader2, AlertCircle, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const NAVY  = 'hsl(var(--c4p-navy-900))';
const ROLES = ['SALES', 'MANAGER', 'SUPER_ADMIN'];
const ROLE_LABELS = { SALES: 'Partner Success Manager', MANAGER: 'Manager', SUPER_ADMIN: 'Super Admin' };
const ROLE_COLORS = {
  SALES:      { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' },
  MANAGER:    { bg: '#fefce8', color: '#713f12', border: '#fde68a' },
  SUPER_ADMIN:{ bg: '#fef2f2', color: '#7f1d1d', border: '#fecaca' },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '\u2014';

const RoleBadge = ({ role }) => {
  const s = ROLE_COLORS[role] || ROLE_COLORS.SALES;
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
};

export default function UsersAdmin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'SALES', password: '' });
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      await adminAPI.createUser(form);
      toast.success(`${form.email} created - user will reset password on first login`);
      setShowCreate(false); setForm({ name: '', email: '', role: 'SALES', password: '' });
      load();
    } catch (ex) { setErr(ex.response?.data?.detail || 'Failed to create user'); }
    finally { setSaving(false); }
  };

  const quickToggleActive = async (e, u) => {
    e.stopPropagation();
    try {
      await adminAPI.updateUser(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? `${u.name} deactivated` : `${u.name} activated`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>User Management</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
            {users.length} users · click a row to view full profile, partners and quotes
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}
          style={{ background: 'hsl(var(--c4p-orange-600))', color: 'white' }}
          className="gap-2" data-testid="admin-create-user-button">
          <Plus size={15} /> Add User
        </Button>
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead>Name</TableHead>
              <TableHead>Login Email <span className="text-xs font-normal opacity-60">/ Contact Email</span></TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Member Since</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            )) : users.map(u => (
              <TableRow
                key={u.id}
                className="data-table-row cursor-pointer"
                style={{ opacity: u.is_active !== false ? 1 : 0.55 }}
                onClick={() => navigate(`/admin/users/${u.id}`)}
                data-testid={`user-row-${u.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'hsl(218 52% 12%)' }}
                    >
                      {u.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="font-medium">{u.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs px-1 py-0 rounded font-medium"
                        style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>Okta</span>
                      <span style={{ color: 'hsl(var(--foreground))' }}>{u.email}</span>
                    </div>
                    {u.psm_direct_email ? (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs px-1 py-0 rounded font-medium"
                          style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>Contact</span>
                        <span style={{ color: 'hsl(var(--muted-fg))' }}>{u.psm_direct_email}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs px-1 py-0 rounded font-medium"
                          style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' }}>Contact</span>
                        <span className="text-xs italic" style={{ color: 'hsl(var(--muted-fg))' }}>not set</span>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell><RoleBadge role={u.role} /></TableCell>
                <TableCell>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={u.is_active !== false
                      ? { background: '#f0fdf4', color: '#14532d', border: '1px solid #bbf7d0' }
                      : { background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb' }
                    }>
                    {u.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{fmtDate(u.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <Button
                      variant="ghost" size="sm"
                      onClick={e => quickToggleActive(e, u)}
                      title={u.is_active !== false ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-user-${u.id}`}
                    >
                      {u.is_active !== false
                        ? <UserX size={13} style={{ color: 'hsl(var(--danger))' }} />
                        : <UserCheck size={13} style={{ color: '#15803d' }} />
                      }
                    </Button>
                    <ChevronRight size={15} style={{ color: 'hsl(var(--muted-fg))' }} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create dialog - keep as dialog (quick add) */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Add New User</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {err && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Full Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="create-user-name" />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required data-testid="create-user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Temporary Password</Label>
                <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={8} placeholder="Min 8 chars" data-testid="create-user-password" />
              </div>
            </div>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>User will be required to reset their password on first login.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ background: NAVY, color: 'white' }} data-testid="create-user-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
